import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { indHeroOne } from "../../../../../images";
import Button from "../../../components/ui/Button";
import { WebPaths } from "../../../../../routes/data";
import { FaArrowRight } from "react-icons/fa6";
import {
  Star,
  CheckCircle2,
  TrendingUp,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { US, GB, CA, AU, EU } from "country-flag-icons/react/3x2";

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-white-f9 overflow-hidden">
      <div className="relative isolate pt-14 lg:pt-24 pb-16 lg:pb-24">
        {/* Background Gradients */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 pointer-events-none"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-blue-ea to-blue-00 opacity-20 sm:left-[calc(50%-30rem)] sm:w-288.75"
          />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            {/* Left Column: Text & CTA */}
            <div className="lg:col-span-5 flex flex-col justify-center text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center justify-center lg:justify-start gap-2 mb-8"
              >
                <div className="flex text-amber-fF">
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                </div>
                <span className="text-dark-6b font-medium text-sm">
                  4.8/5 · 1,000+ success stories
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-balance text-5xl md:text-6xl font-light font-spartan tracking-tight text-black-1a leading-[1.05]"
              >
                Empower Your <br className="hidden lg:block" />
                Dreams, Get Your Visa{" "}
                <span className="inline-flex bg-blue-00 border-8 border-[#eaf5ff] text-white px-3 pb-1 rounded-2xl rotate-2 hover:rotate-0 italic mx-1 shadow-md">
                  Approved
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 text-pretty text-lg text-dark-6b sm:text-xl leading-relaxed mx-auto lg:mx-0 max-w-xl"
              >
                We combine technology with expert support to maximize your U.S.
                visa approval chances. Build a tailored application plan to help
                you succeed and feel your best.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-10 flex flex-col gap-5 max-w-md mx-auto lg:mx-0 text-left"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-black-31 shrink-0" />
                  <span className="text-black-1a font-medium">
                    Up to 3x higher approval odds
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-black-31 shrink-0" />
                  <span className="text-black-1a font-medium">
                    AI-powered error checking
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-black-31 shrink-0" />
                  <span className="text-black-1a font-medium">
                    Direct access to immigration experts
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-4"
              >
                <Button
                  label="Check my eligibility"
                  onClick={() => navigate(`${WebPaths.checkEligibility}/ds160`)}
                  variant="primary"
                  size="large"
                  endIcon={<FaArrowRight />}
                />
              </motion.div>
            </div>

            {/* Right Column: Bento Grid Graphics */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-7 mt-16 lg:mt-0 relative w-full h-full min-h-[450px] lg:min-h-[550px] flex flex-col justify-center"
            >
              <div className="grid grid-cols-2 gap-4 w-full lg:py-6">
                {/* Large spotlight card top */}
                <div className="col-span-2 overflow-hidden rounded-[2rem] h-[280px] sm:h-[340px] relative group bg-white border border-transparent shadow-md transition-all duration-300 hover:border-blue-ea hover:shadow-xl hover:-translate-y-1">
                  <img
                    src={indHeroOne}
                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-[1.03]"
                    alt="Happy professional with a visa"
                  />
                  {/* Floating UI Element */}
                  <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-md px-5 py-2.5 rounded-full border border-grey-eb shadow-lg flex items-center gap-2 transform transition-transform duration-500 hover:-translate-y-1">
                    <FileText className="w-4 h-4 text-blue-00" />
                    <span className="text-xs font-bold text-black-31 uppercase tracking-wide">
                      Approved
                    </span>
                  </div>
                </div>

                {/* Small utility card bottom left */}
                <div className="col-span-1 overflow-hidden rounded-[1.5rem] h-40 sm:h-48 relative group bg-white border border-transparent shadow-sm transition-all duration-300 hover:border-blue-ea hover:shadow-xl hover:-translate-y-1">
                  <img
                    src="https://images.unsplash.com/photo-1569098644584-210bcd375b59?q=80&w=3000&auto=format&fit=crop"
                    className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-[1.03]"
                    alt="Travel planning context"
                  />
                </div>

                {/* Small utility card bottom right */}
                <div className="col-span-1 overflow-hidden rounded-[1.5rem] h-40 sm:h-48 relative group bg-white border border-transparent shadow-sm transition-all duration-300 hover:border-blue-ea hover:shadow-xl hover:-translate-y-1">
                  <img
                    src="https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=3000&auto=format&fit=crop"
                    className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-[1.03]"
                    alt="Using mobile app context"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Full-width Supported Countries Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="w-full border-t border-grey-eb mt-20 lg:mt-24 pt-6 pb-6 bg-white/50 backdrop-blur-sm lg:bg-transparent"
        >
          <div className="flex items-center w-full">
            {/* Static Left Side Container */}
            <div className="flex-none px-6 lg:pl-8 lg:pr-12 border-r border-grey-eb flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-ea flex items-center justify-center">
                <span className="text-blue-00 text-lg">🌍</span>
              </div>
              <p className="hidden md:block text-sm font-bold text-black-1a tracking-wide uppercase">
                Supported Destinations
              </p>
            </div>

            {/* Sliding Right Side Container */}
            <div className="flex-1 overflow-hidden pointer-events-none">
              <Marquee gradient={false} speed={40} className="overflow-hidden">
                <div className="flex items-center gap-12 pr-12">
                  <div className="flex items-center gap-3">
                    <US
                      title="United States"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      United States
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <GB
                      title="United Kingdom"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      United Kingdom
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CA
                      title="Canada"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      Canada
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <AU
                      title="Australia"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      Australia
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <EU
                      title="Schengen Area"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      Schengen Area
                    </span>
                  </div>
                  {/* Repeated buffer to ensure smooth scrolling */}
                  <div className="flex items-center gap-3">
                    <US
                      title="United States"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      United States
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <GB
                      title="United Kingdom"
                      className="w-6 h-4 rounded-sm shadow-sm"
                    />
                    <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                      United Kingdom
                    </span>
                  </div>
                </div>
              </Marquee>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
